import React, {useEffect, useState, useCallback} from "react";
import {useDispatch, useSelector} from 'react-redux';
import axios from "axios";
import "./club-finder-map-page.scss";
import GoogleMapCluster from "../../molecules/google-map-cluster/google-map-cluster";
import ClubResultList from "../../molecules/club-result-list/club-result-list";
import FilterComponent from "../../molecules/filter-component/filter-component";
import ClubFinderSingleResult from "../../molecules/club-finder-single-result/club-finder-single-result";
import ClubFinderSingleResultVersion2
  from "../../version-2/molecules/club-finder-single-result/club-finder-single-result";
import {AppVersionForTenants, Config} from "../../../constants/Config";
import {clubFinderMapResultSliceActions} from "../../../store/slices/clubFinderResultMapSlice";
import ClubPlaceholderContent from "../../atoms/club-placeholder-content/club-placeholder-content";
import {RootState} from "../../../store/configureStore";
import {clubFinderSearchBarSliceActions} from "../../../store/slices/clubFinderSearchBarSlice";
import Loader from "../../atoms/Loader/loader";

const MobileViewWidth = Config.breakpoints.large.width;
const extraLargeWidth = Config.breakpoints.extraLarge.width;

interface MapPagePropTypes {
  finderId: string | undefined,
  presetOrgId?: string,
}

const ClubFinderMapPage = (props: MapPagePropTypes): JSX.Element => {
  const dispatch = useDispatch();
  const parentWidth = useSelector((state: RootState) => state.widgetConfigurationSettings.widgetParentWidth);
  const mapCoordinates = useSelector((state: RootState) => state.clubFinderMapResultStore.mapCoordinates);
  const selectedFormatFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedFormat);
  const selectedGenderFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedGenderFilter);
  const clubFinderListResult = useSelector((state: RootState) => state.clubFinderMapResultStore.clubFinderListResult);
  const isMobileSearchVisible = useSelector((state: RootState) => state.clubFinderSearchBarStore.isMobileSearchVisible);
  const googleMapConfig = useSelector((state: RootState) => state.widgetConfigurationSettings.googleMapConfig);
  const selectedLocation = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedLocation);
  const tenant = useSelector((state: RootState) => state.widgetConfigurationSettings.tenant);
  const searchButtonPressed = useSelector((state: RootState) => state.clubFinderSearchBarStore.searchButtonPressed);
  const isMapExpanded = useSelector((state: RootState) => state.clubFinderMapResultStore.isMapExpanded);
  const toggleUserLocationFunction = useSelector((state: RootState) => state.clubFinderMapResultStore.toggleUserLocationFunction);
  const [clubFinderResult, setClubFinderResult] = useState(null);
  const [clubFinderSingleResult, setClubFinderSingleResult] = useState(null);
  // @ts-ignore
  const [updatedZoom, updateMapZoom] = useState(Config.map.defaultZoom[tenant]);
  // @ts-ignore
  const [updatedCenter, updateCenter] = useState(Config.map.defaultCenter[tenant]);
  const [clubResultType, setClubResultType] = useState('');
  const [isLoading, setLoader] = useState(false);
  const [clubResultContainerWidth, setClubResultContainerWidth] = useState(0);
  const selectedInclusionFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedInclusionFilters);
  const selectedDaysFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedDayFilters);
  const [userLocation, setUserLocation] = useState<{ userLat: number | null, userLng: number | null }>({
    userLat: null,
    userLng: null
  });
  const userLocationAddress = useSelector((state: RootState) => state.clubFinderSearchBarStore.userLocationAddress);
  const [isSearchResultVisibleMobile, setSearchResultVisibleMobile] = useState(false);
  const selectedMultiGenderFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedMultiGenderFilters)
  const applyClickedCounter = useSelector((state: RootState) => state.clubFinderSearchBarStore.applyClickedCounter);
  const floatingSearchBarWidth = useSelector((state: RootState) => state.widgetConfigurationSettings.floatingBarWidth);

  const getClubList = useCallback((apiUrl: string) => {
    axios.get(apiUrl, {headers: {'tenant': tenant}}).then(async (resp) => {
      dispatch(clubFinderMapResultSliceActions.setClubFinderListResult(resp.data.organisations));
      setClubFinderSingleResult(null);
      // @ts-ignore
      updateMapZoom(Config.map.clusterZoom[tenant]);
      setClubResultType('club-list-result');
      setLoader(false);
    }).catch(e => {
      console.log("Error while getting club list:", e);
      setLoader(false);
    });
  }, [dispatch, tenant]);

  const prepareClubListData = useCallback((
    latitude: any,
    longitude: any,
    apiUrl: string,
    selectedFormatFilter: any,
    selectedGenderFilter: any,
    selectedDaysFilter: any,
    selectedInclusionFilter: any) => {
    try {
      let formats = '';
      let formatParam = null;
      if (selectedFormatFilter) {
        for (let i = 0; i < selectedFormatFilter.length; i++) {
          formats = formats.concat(`${selectedFormatFilter[i]}${i < selectedFormatFilter.length - 1 ? ',' : ''}`)
        }
      }
      formatParam = `&format=${formats}`
      const selectedDaysParams = selectedDaysFilter ? selectedDaysFilter.join(',') : null
      const selectedInclusionParams = selectedInclusionFilter ? selectedInclusionFilter.map((inclusionOption: any) => inclusionOption.inclusionPlayHQValue).join(',') : null;

      let genderParam;
      if (tenant === 'nzc') {
        genderParam = selectedGenderFilter && selectedGenderFilter.type && !(selectedGenderFilter.type === 'anyone') ? selectedGenderFilter.type : '';
      } else if (AppVersionForTenants.v2.tenants.includes(tenant)) {
        const maleExists = selectedGenderFilter ? selectedGenderFilter?.some((item: any) => item.type === "MALE") : null;
        const femaleExists = selectedGenderFilter ? selectedGenderFilter?.some((item: any) => item.type === "FEMALE") : null;
        const anyoneExits = selectedGenderFilter ? selectedGenderFilter?.some((item: any) => item.type === Config.defaultGenderOption.type) : null;

        if (selectedGenderFilter && anyoneExits) {
          genderParam = null;
        } else if (femaleExists && maleExists) {
          genderParam = "FEMALE,MALE";
        } else if (femaleExists || maleExists) {
          genderParam = femaleExists ? "FEMALE" : "MALE";
        }
      }

      apiUrl = `${apiUrl}?finderId=${props.finderId}&lat=${latitude}&lng=${longitude}&map_key=${googleMapConfig.mapsAPIKey}${formats && selectedFormatFilter && !selectedFormatFilter.includes('All Format') ? formatParam : ''}${selectedGenderFilter && genderParam ? `&gender=${genderParam}` : ''}${selectedDaysParams ? `&days=${selectedDaysParams}` : ''}${selectedInclusionParams ? `&inclusions=${selectedInclusionParams}` : ''}`;
      updateCenter({lat: parseFloat(latitude), lng: parseFloat(longitude)});
      getClubList(apiUrl);
      dispatch(clubFinderMapResultSliceActions.setMapCoordinates({longitude, latitude}));
    } catch (e) {
      console.log("Error while preparing club list data: ", e);
      setLoader(false);
    }
  }, [dispatch, getClubList, googleMapConfig.mapsAPIKey]);

  const getRegionDetails = useCallback((
    placeId: string = '',
    selectedFormatFilter: any = null,
    selectedGenderFilter: any = null,
    lat: any = null,
    lng: any = null,
    selectedDaysFilter: any = null, selectedInclusionFilter: any = null) => {
    let apiUrl = `${Config.baseUrl}${Config.apiEndpoints.v1.findResults}`;
    let locationDetails;
    let location;
    let longitude = lng;
    let latitude = lat;
    if (placeId) {
      const googleDetailsApi = `${Config.baseUrl}${Config.apiEndpoints.v1.findResults}?place_id=${placeId}&map_key=${googleMapConfig.mapsAPIKey}`;
      setLoader(true);
      axios.get(googleDetailsApi, {headers: {'tenant': tenant}}).then(async (resp) => {
        locationDetails = resp.data;
        location = locationDetails.result.geometry.location;
        longitude = location.lng;
        latitude = location.lat;
        if (latitude && longitude) {
          prepareClubListData(latitude, longitude, apiUrl, selectedFormatFilter, selectedGenderFilter, selectedDaysFilter, selectedInclusionFilter);
        }
      }).catch(e => {
        setLoader(false);
        console.log("Google Map Details API", e);
      });
    } else if (lat && lng) {
      setLoader(true);
      prepareClubListData(latitude, longitude, apiUrl, selectedFormatFilter, selectedGenderFilter, selectedDaysFilter, selectedInclusionFilter);
    }
  }, [tenant, prepareClubListData, googleMapConfig.mapsAPIKey])

  const getClubDetails = useCallback((orgId: string, distance: string = '') => {
    dispatch(clubFinderMapResultSliceActions.setIsMapExpanded(false));
    if (!props.presetOrgId && AppVersionForTenants.v2.tenants.includes(tenant)) {
      manageOrgIdURLParam("set", orgId);
    }
    try {
      setLoader(true);
      axios.get(`${Config.baseUrl}${Config.apiEndpoints.v1.clubDetails}?orgId=${orgId}&finderId=${props.finderId}`,
        {headers: {'tenant': tenant}}).then(async (resp) => {
        if (resp.data && resp.data.data) {
          if (distance) {
            resp.data.data.distance = distance;
          }
          setClubFinderSingleResult(resp.data.data);
          const latitude = resp.data.data.address.latitude;
          const longitude = resp.data.data.address.longitude;
          updateCenter({lat: parseFloat(latitude), lng: parseFloat(longitude)})
          updateMapZoom(Config.map.singleResultZoom);
          setClubResultType('single-club-result')
        }
        setLoader(false);
      }).catch(e => {
        console.log("Error finding club:", e);
        setLoader(false);
      });
    } catch (e) {
      setLoader(false);
      console.log("Error finding club:", e);
    }
  }, [tenant, dispatch, props.finderId])

  useEffect(() => {
    const savedGenderOptionFilters = tenant === 'nzc' ? selectedGenderFilter : selectedMultiGenderFilter;

    if (selectedLocation) {
      // If selected location is an organisation.
      if (selectedLocation.orgName && selectedLocation.orgId) {
        getClubDetails(selectedLocation.orgId);
        dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(false))
      } else if (selectedLocation.address) {
        // If selected location is a region
        if (selectedLocation.placeId) {
          if (parentWidth > MobileViewWidth) {
            getRegionDetails(selectedLocation.placeId, selectedFormatFilter, savedGenderOptionFilters, null, null, selectedDaysFilter, selectedInclusionFilter);
          } else if (parentWidth <= MobileViewWidth && searchButtonPressed) {
            setClubResultType('club-list-result');
            getRegionDetails(selectedLocation.placeId, selectedFormatFilter, savedGenderOptionFilters, null, null, selectedDaysFilter, selectedInclusionFilter);
          }
        } else if (selectedLocation.address.latitude && selectedLocation.address.longitude) {
          dispatch(clubFinderSearchBarSliceActions.setIsSearchable(false));
          if (AppVersionForTenants.v2.tenants.includes(tenant) && userLocationAddress && userLocationAddress.suburb) {
            dispatch(clubFinderSearchBarSliceActions.setIsSearchable(true));
          }
          if (parentWidth > MobileViewWidth) {
            getRegionDetails('', selectedFormatFilter, savedGenderOptionFilters, selectedLocation.address.latitude, selectedLocation.address.longitude, selectedDaysFilter, selectedInclusionFilter);
          } else if (parentWidth <= MobileViewWidth && searchButtonPressed) {
            setClubResultType('club-list-result');
            getRegionDetails('', selectedFormatFilter, savedGenderOptionFilters, selectedLocation.address.latitude, selectedLocation.address.longitude, selectedDaysFilter, selectedInclusionFilter);
          }
        }
      }
    } else if (props.presetOrgId) {
      getClubDetails(props.presetOrgId);
      dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(false));
    } else {
      setClubResultType('');
      setClubFinderSingleResult(null);
      dispatch(clubFinderMapResultSliceActions.setMapCoordinates({
        // @ts-ignore
        latitude: Config.map.defaultCenter[tenant].lat,
        // @ts-ignore
        longitude: Config.map.defaultCenter[tenant].lng
      }))
    }
  }, [dispatch,
    selectedLocation,
    selectedLocation?.address.latitude,
    selectedLocation?.address.longitude,
    getClubDetails,
    getRegionDetails,
    parentWidth,
    searchButtonPressed,
    applyClickedCounter,
    props.presetOrgId
  ]);

  useEffect(() => {
    setClubFinderResult(clubFinderListResult);
  }, [clubFinderListResult])

  const manageOrgIdURLParam = (action: 'set' | 'unset', orgId: string = '') => {
    const currentURL = new URL(window.location.href);
    if (action === 'unset') {
      if (currentURL.searchParams.has(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].orgId)) {
        currentURL.searchParams.delete(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].orgId);
      }
    } else if (action === 'set') {
      currentURL.searchParams.set(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].orgId, orgId)
    }

    window.history.replaceState({}, document.title, currentURL.href);
  }
  const backButtonAction = () => {
    if (clubFinderResult) {
      setClubFinderSingleResult(null);
      updateCenter({lat: mapCoordinates.latitude || Config.map.defaultCenter[tenant].lat, lng: mapCoordinates.longitude || Config.map.defaultCenter[tenant].lng});
      // @ts-ignore
      updateMapZoom(Config.map.clusterZoom[tenant]);
      setClubResultType('club-list-result');
    } else if (clubFinderSingleResult &&  AppVersionForTenants.v2.tenants.includes(tenant) && (parentWidth > MobileViewWidth)) {
      setClubFinderSingleResult(null);
      dispatch(clubFinderSearchBarSliceActions.setSelectedLocation(null));
      updateCenter({lat: Config.map.defaultCenter[tenant].lat, lng: Config.map.defaultCenter[tenant].lng});
      updateMapZoom(Config.map.controlledAreaZoom[tenant]);
    } else if ((parentWidth <= MobileViewWidth) && clubFinderSingleResult && !clubFinderResult) {
      if (AppVersionForTenants.v2.tenants.includes(tenant)) {
        setClubFinderSingleResult(null);
        setClubResultType('');
        dispatch(clubFinderSearchBarSliceActions.setSelectedLocation(null));
      }
      dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(!isMobileSearchVisible))

      // If there is need to get back to original state of map after filter is closed below actions are to be performed
      // setClubFinderSingleResult(null);
      // setClubResultType('');
      // dispatch(clubFinderSearchBarSliceActions.setSelectedLocation(null));
    }
    manageOrgIdURLParam("unset");
  }

  let rawDataPoints: object | null = [];
  if (!clubFinderSingleResult) {
    rawDataPoints = clubFinderResult ? clubFinderResult : [];
  } else {
    rawDataPoints = clubFinderSingleResult ? [clubFinderSingleResult] : [];
  }

  useEffect(() => {
    if(AppVersionForTenants.v2.tenants.includes(tenant)) {
      if (parentWidth <= MobileViewWidth) {
        dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(true));
      }
      if (parentWidth > MobileViewWidth && parentWidth <= extraLargeWidth) {
        if(floatingSearchBarWidth == parentWidth) {
          setClubResultContainerWidth((parentWidth*50)/100);
        } else {
          setClubResultContainerWidth(floatingSearchBarWidth);
        }
      } else
      if (parentWidth > extraLargeWidth) {
        if(floatingSearchBarWidth == parentWidth) {
          setClubResultContainerWidth((parentWidth*34.5)/100);
        } else {
          setClubResultContainerWidth(floatingSearchBarWidth);
        }
      }
    }
  }, [parentWidth, MobileViewWidth, tenant, floatingSearchBarWidth])

  // Function to get user's location
  const getUserCurrentLocation = () => {
    dispatch(clubFinderSearchBarSliceActions.setUserLocationSearchingState(true));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const {latitude, longitude} = position.coords;
          setUserLocation(
            {'userLat': latitude, 'userLng': longitude}
          )
          let apiUrl = `${Config.baseUrl}${Config.apiEndpoints.v1.findResults}`;
          prepareClubListData(latitude, longitude, apiUrl, selectedFormatFilter, selectedMultiGenderFilter, selectedDaysFilter, selectedInclusionFilter);
          dispatch(clubFinderMapResultSliceActions.setMapCoordinates({
            latitude: latitude,
            longitude: longitude
          }))
          dispatch(clubFinderSearchBarSliceActions.setUserLocationSearchingState(false));
        },
        (error) => {
          dispatch(clubFinderSearchBarSliceActions.setUserLocationSearchingState(false));
          console.error('Error getting user location:', error);
        }
      );
    } else {
      dispatch(clubFinderSearchBarSliceActions.setUserLocationSearchingState(false));
      console.error('Geolocation is not supported by this browser.');
    }
  };

  useEffect(() => {
    if (AppVersionForTenants.v2.tenants.includes(tenant)) {
      const currentURL = new URL(window.location.href);
      //Do not get user location, if url params have orgID.
      if(!currentURL.searchParams.has(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].orgId)) {
        getUserCurrentLocation();
      }
    }
  }, [toggleUserLocationFunction, tenant])

  useEffect(() => {
    if (parentWidth <= MobileViewWidth && AppVersionForTenants.v2.tenants.includes(tenant)) {
      setSearchResultVisibleMobile(!isMobileSearchVisible)
    }
  }, [isMobileSearchVisible, parentWidth, MobileViewWidth]);

  return (
    <div className={`clubFinderMapPageContainer clubFinderMapPageContainer--${clubResultType}`}>
      <div
        className={`finderResultContainer ${tenant} ${isMapExpanded ? 'finderResultContainer--map-expanded' : 'finderResultContainer--map-collapsed'}
           ${AppVersionForTenants.v2.tenants.includes(tenant) ? 'version-2-result-container' : ''}  ${(AppVersionForTenants.v2.tenants.includes(tenant) && (clubFinderSingleResult || clubFinderResult)) ? 'version-2-result-container--visible' : 'version-2-result-container--invisible'}
           ${isSearchResultVisibleMobile ? 'mobile-list-visible' : 'mobile-list-hidden'}`}
        style={{minWidth: AppVersionForTenants.v2.tenants.includes(tenant) ? `${clubResultContainerWidth}px` : 'unset'}}>
        {(!clubFinderSingleResult && clubFinderResult) && !isLoading && (
          <>
            <FilterComponent/>
            <ClubResultList clubFinderResult={clubFinderResult ? clubFinderResult : null}
                            handleClubTileAction={(orgId: string, distance: string) => getClubDetails(orgId, distance)}
                            tenant={tenant}/>
          </>
        )}

        {clubFinderSingleResult && !isLoading && tenant == 'nzc' && (
          <ClubFinderSingleResult
            // @ts-ignore
            clubFinderSingleResult={clubFinderSingleResult}
            buttonActionHandle={backButtonAction}
            backButtonEnabled={!(parentWidth <= MobileViewWidth) ? !!clubFinderResult : true}
            tenant={tenant}
          />
        )}

        {clubFinderSingleResult && !isLoading && AppVersionForTenants.v2.tenants.includes(tenant) && (
          <ClubFinderSingleResultVersion2
            // @ts-ignore
            clubFinderSingleResult={clubFinderSingleResult}
            buttonActionHandle={backButtonAction}
            backButtonEnabled={!(parentWidth <= MobileViewWidth) ? !!clubFinderResult : true}
            tenant={tenant}
          />
        )}

        {selectedLocation === null && !clubFinderSingleResult && !clubFinderResult && !isLoading && (
          <ClubPlaceholderContent placeHolderText={Config.search.noSearchPlaceholder} tenant={tenant}/>
        )}

        {isLoading && (
          <>
            <Loader/>
          </>
        )}
      </div>

      <div
        className={`mapContainer mapContainer--${clubResultType} ${tenant} ${isMapExpanded ? 'mapContainer--map-expanded' : 'mapContainer--map-collapsed'} ${AppVersionForTenants.v2.tenants.includes(tenant) ? 'mapContainer--full-width' : ''}`}>
        <GoogleMapCluster
          singleResultMarker={!!(clubFinderSingleResult && clubFinderSingleResult !== null)}
          updatedZoom={updatedZoom}
          newCenter={updatedCenter}
          rawDataPoints={rawDataPoints}
          mapMarkerClickAction={(orgId: string, distance: string) => getClubDetails(orgId, distance)}
          userLocation={userLocation}/>
      </div>
    </div>
  )

}

export default ClubFinderMapPage;
